import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { isViewer } from "../utils/permissions";

const KitchenLeavePolicy = () => {
  const [kitchens, setKitchens] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [selectedKitchen, setSelectedKitchen] = useState("");
  const [weeklyOffDay, setWeeklyOffDay] = useState("6");
  const [paidOffs, setPaidOffs] = useState("2");
  const [earnedLeaves, setEarnedLeaves] = useState("0");
  const [casualLeaves, setCasualLeaves] = useState("0");
  const [medicalLeaves, setMedicalLeaves] = useState("0");
  const [loading, setLoading] = useState(false);

  const weekDays = [
    { value: 0, label: "Monday" },
    { value: 1, label: "Tuesday" },
    { value: 2, label: "Wednesday" },
    { value: 3, label: "Thursday" },
    { value: 4, label: "Friday" },
    { value: 5, label: "Saturday" },
    { value: 6, label: "Sunday" },
    { value: 7, label: "All Days" },
  ];

  useEffect(() => {
    fetchKitchens();
    fetchPolicies();
  }, []);

  const fetchKitchens = async () => {
    try {
      const res = await axios.get(
        "https://ims-attendanceback.indevconsultancy.in/api/kitchen-list"
      );
      setKitchens(res.data.kitchens || []);
    } catch (error) {
      console.error("Error fetching kitchens:", error);
    }
  };

  const fetchPolicies = async () => {
    try {
      const res = await axios.get(
        "https://ims-attendanceback.indevconsultancy.in/api/kitchen-leave-policy/"
      );
      setPolicies(res.data.data || []);
    } catch (error) {
      console.error("Error fetching policies:", error);
    }
  };

  const handleKitchenSelect = async (kitchenId) => {
    setSelectedKitchen(kitchenId);
    
    const resetDefaults = () => {
      setWeeklyOffDay("6");
      setPaidOffs("2");
      setEarnedLeaves("0");
      setCasualLeaves("0");
      setMedicalLeaves("0");
    };

    if (!kitchenId) {
      resetDefaults();
      return;
    }

    // Load existing policy for this kitchen
    try {
      const res = await axios.get(
        `https://ims-attendanceback.indevconsultancy.in/api/kitchen-leave-policy/?kitchen_id=${kitchenId}`
      );
      if (res.data.status && res.data.data) {
        const d = res.data.data;
        setWeeklyOffDay(d.weekly_off_day.toString());
        setPaidOffs(d.paid_offs_per_month.toString());
        setEarnedLeaves((d.earned_leaves_per_year ?? 0).toString());
        setCasualLeaves((d.casual_leaves_per_year ?? 0).toString());
        setMedicalLeaves((d.medical_leaves_per_year ?? 0).toString());
      }
    } catch (error) {
      // No policy exists yet, keep defaults
      resetDefaults();
    }
  };

  const handleSave = async () => {
    if (!selectedKitchen) {
      Swal.fire("Error", "Please select a kitchen", "error");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(
        "https://ims-attendanceback.indevconsultancy.in/api/kitchen-leave-policy/",
        {
          kitchen_id: selectedKitchen,
          weekly_off_day: parseInt(weeklyOffDay),
          paid_offs_per_month: parseInt(paidOffs),
          earned_leaves_per_year: parseInt(earnedLeaves) || 0,
          casual_leaves_per_year: parseInt(casualLeaves) || 0,
          medical_leaves_per_year: parseInt(medicalLeaves) || 0,
        }
      );

      if (res.data.status) {
        Swal.fire("Success!", res.data.message, "success");
        fetchPolicies();
        setSelectedKitchen("");
        setWeeklyOffDay("6");
        setPaidOffs("2");
        setEarnedLeaves("0");
        setCasualLeaves("0");
        setMedicalLeaves("0");
      }
    } catch (error) {
      Swal.fire("Error", error.response?.data?.message || "Failed to save policy", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-3 py-3 bg-cream" style={{ minHeight: "100vh" }}>
      <div className="card">
        <div className="card-header bg-skyBlue text-white">
          <h5 className="mb-0">Kitchen Leave Policy Management</h5>
        </div>
        <div className="card-body">
          {/* Policy Form */}
          <div className="row mb-4">
            <div className="col-md-12">
              <div className="card border-primary">
                <div className="card-body">
                  <h6 className="card-title mb-3">Set Kitchen Policy</h6>
                  
                  <div className="row g-3">
                    <div className="col-md-4">
                      <label className="form-label">Select Kitchen *</label>
                      <select
                        className="form-select"
                        value={selectedKitchen}
                        onChange={(e) => handleKitchenSelect(e.target.value)}
                      >
                        <option value="">-- Select Kitchen --</option>
                        {kitchens.map((k) => (
                          <option key={k.kitchenid} value={k.kitchenid}>
                            {k.kitchen_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">Weekly Off Day *</label>
                      <select
                        className="form-select"
                        value={weeklyOffDay}
                        onChange={(e) => setWeeklyOffDay(e.target.value)}
                      >
                        {weekDays.map((day) => (
                          <option key={day.value} value={day.value}>
                            {day.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-4">
                      <label className="form-label">Paid Offs Per Month *</label>
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        max="10"
                        value={paidOffs}
                        onChange={(e) => setPaidOffs(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Earned Leaves / Year</label>
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        value={earnedLeaves}
                        onChange={(e) => setEarnedLeaves(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Casual Leaves / Year</label>
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        value={casualLeaves}
                        onChange={(e) => setCasualLeaves(e.target.value)}
                      />
                    </div>
                    {/* Medical Leaves / Year — hidden for now, unhide later */}
                    {/* <div className="col-md-4">
                      <label className="form-label">Medical Leaves / Year</label>
                      <input
                        type="number"
                        className="form-control"
                        min="0"
                        value={medicalLeaves}
                        onChange={(e) => setMedicalLeaves(e.target.value)}
                      />
                    </div> */}
                  </div>

                  <div className="mt-3">
                   <button
                      className="btn btn-primary"
                      onClick={handleSave}
                      disabled={loading || isViewer()}
                      title={isViewer() ? "View-only role: action not allowed" : undefined}
                      style={isViewer() ? { opacity: 0.55, cursor: "not-allowed" } : undefined}
                    >
                      {loading ? "Saving..." : "Save Policy"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Existing Policies Table */}
          <div className="table-responsive">
            <h6 className="mb-3">Current Kitchen Policies</h6>
            <table className="table table-bordered table-striped">
              <thead className="table-light">
                <tr>
                  <th>S.No</th>
                  <th>Kitchen Name</th>
                  <th>Weekly Off Day</th>
                  <th>Paid Offs/Month</th>
                  <th>EL / Year</th>
                  <th>CL / Year</th>
                   {/* <th>ML / Year</th>  // hidden for now */}
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {policies.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      No policies configured yet
                    </td>
                  </tr>
                ) : (
                  policies.map((policy, index) => (
                    <tr key={policy.kitchen_id}>
                      <td>{index + 1}</td>
                      <td>{policy.kitchen_name}</td>
                      <td>{policy.weekly_off_day_display}</td>
                      <td>{policy.paid_offs_per_month}</td>
                      <td>{policy.earned_leaves_per_year ?? 0}</td>
                      <td>{policy.casual_leaves_per_year ?? 0}</td>
                      {/* <td>{policy.medical_leaves_per_year ?? 0}</td>  // hidden for now */}
                      <td>
                        <span className="badge bg-success">Active</span>
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => handleKitchenSelect(policy.kitchen_id)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KitchenLeavePolicy;